import { useEffect, useState } from "react";
import { CalculatorInputs, CalculationOutput } from "../types";
import { saveSimulation, SimulationRecord } from "../lib/simulationsStore";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { captureEvent } from "../lib/analytics";

interface Props {
  inputs: CalculatorInputs;
  output: CalculationOutput | null;
  currentSimulationId: string | null;
  onClose: () => void;
  onSaved: () => void;
  onShowLogin: () => void;
}

export default function SaveSimulationDialog({
  inputs,
  output,
  currentSimulationId,
  onClose,
  onSaved,
  onShowLogin,
}: Props) {
  const { user } = useAuth();
  const [clientName, setClientName] = useState("");
  const [clientNif, setClientNif] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadExistingData = async () => {
      if (currentSimulationId && user) {
        try {
          const { data, error } = await supabase
            .from('simulaciones')
            .select('client_name, client_nif')
            .eq('id', currentSimulationId)
            .maybeSingle();

          if (!error && data) {
            setClientName(data.client_name || '');
            setClientNif(data.client_nif || '');
          }
        } catch (err) {
          console.error('Error loading simulation data:', err);
        }
      }
    };

    loadExistingData();
  }, [currentSimulationId, user]);

  const handleSave = async () => {
    if (!clientName.trim() || !output) return;

    setSaving(true);
    setError(null);

    try {
      const brutoAnual = output.brutoFacturado ?? 0;
      const netoFinal = (output as any).netoFinalTrasRenta ?? output.netoFiscalAnual ?? 0;

      if (currentSimulationId && user) {
        const { error: updateError } = await supabase
          .from('simulaciones')
          .update({
            client_name: clientName.trim(),
            client_nif: clientNif.trim(),
            inputs: inputs,
            bruto_anual: brutoAnual,
            output_neto: netoFinal,
          })
          .eq('id', currentSimulationId);

        if (updateError) throw updateError;

        captureEvent('simulation_updated', { year: inputs.year, region: inputs.region });
      } else {
        const rec: SimulationRecord = {
          id: crypto.randomUUID(),
          clientName: clientName.trim(),
          clientNif: clientNif.trim(),
          createdAt: new Date().toISOString(),
          year: inputs.year,
          region: inputs.region,
          brutoFacturado: brutoAnual,
          netoFinalTrasRenta: netoFinal,
          cajaPreviaRenta: (output as any).cajaPreviaRenta ?? 0,
          inputs,
          output,
        };

        await saveSimulation(rec, user);
        captureEvent('simulation_saved', {
          year: inputs.year,
          region: inputs.region,
          isLoggedIn: !!user,
          storage: user ? 'supabase' : 'localStorage',
        });
      }

      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la simulación');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 50,
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            padding: "32px",
            width: "100%",
            maxWidth: "440px",
            margin: "0 16px",
            textAlign: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: 700,
              marginBottom: "12px",
              color: "var(--text)",
            }}
          >
            Iniciar sesión requerido
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "var(--muted)",
              marginBottom: "24px",
              lineHeight: "1.5",
            }}
          >
            Para guardar simulaciones en la nube y acceder desde cualquier dispositivo, necesitas iniciar sesión.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
            }}
          >
            <button
              onClick={onClose}
              style={{
                appearance: "none",
                background: "none",
                border: "1px solid var(--border)",
                borderRadius: "8px",
                padding: "10px 18px",
                fontSize: "13px",
                fontWeight: 600,
                color: "var(--muted)",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                onClose();
                onShowLogin();
              }}
              style={{
                appearance: "none",
                background: "var(--brand)",
                border: "none",
                borderRadius: "8px",
                padding: "10px 18px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              Iniciar sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          padding: "24px",
          width: "100%",
          maxWidth: "440px",
          margin: "0 16px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: 700,
            marginBottom: "16px",
            color: "var(--text)",
          }}
        >
          {currentSimulationId ? 'Actualizar simulación' : 'Guardar simulación'}
        </h3>
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "13px",
              fontWeight: 600,
              marginBottom: "6px",
              color: "var(--text)",
            }}
          >
            Nombre de la simulación
          </label>
          <input
            type="text"
            className="input"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Ej. IRPF 2025 - Escenario A"
            autoFocus
          />
        </div>
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "13px",
              fontWeight: 600,
              marginBottom: "6px",
              color: "var(--text)",
            }}
          >
            NIF / CIF (opcional)
          </label>
          <input
            type="text"
            className="input"
            value={clientNif}
            onChange={(e) => setClientNif(e.target.value)}
            placeholder="Ej. 12345678A"
          />
        </div>

        {error && (
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              appearance: "none",
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              padding: "10px 18px",
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--muted)",
              cursor: saving ? "not-allowed" : "pointer",
              transition: "all 0.15s ease",
              opacity: saving ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.backgroundColor = "var(--bg)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!clientName.trim() || saving}
            style={{
              appearance: "none",
              background: "var(--brand)",
              border: "none",
              borderRadius: "8px",
              padding: "10px 18px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#fff",
              cursor: clientName.trim() && !saving ? "pointer" : "not-allowed",
              transition: "all 0.15s ease",
              opacity: clientName.trim() && !saving ? 1 : 0.5,
            }}
            onMouseEnter={(e) => {
              if (clientName.trim() && !saving) {
                e.currentTarget.style.backgroundColor = "var(--brand-ink)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "var(--brand)";
            }}
          >
            {saving ? (currentSimulationId ? 'Actualizando...' : 'Guardando...') : (currentSimulationId ? 'Guardar Cambios' : 'Guardar')}
          </button>
        </div>
      </div>
    </div>
  );
}
